import React from 'react';
import { useBranding } from '../../contexts/BrandingContext';

// Branded Header Component
interface BrandedHeaderProps {
  showLogo?: boolean;
  showTagline?: boolean;
  customTagline?: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export const BrandedHeader: React.FC<BrandedHeaderProps> = ({
  showLogo = true,
  showTagline = true,
  customTagline,
  size = 'medium',
  className = ''
}) => {
  const { branding } = useBranding();

  const sizeClasses = {
    small: 'mb-4',
    medium: 'mb-6',
    large: 'mb-8'
  };

  const titleSizes = {
    small: 'text-xl',
    medium: 'text-3xl',
    large: 'text-4xl'
  };

  const taglineSizes = {
    small: 'text-sm',
    medium: 'text-lg',
    large: 'text-xl'
  };

  return (
    <div className={`text-center ${sizeClasses[size]} ${className}`}>
      {showLogo && branding.companyLogo && (
        <div className="mb-4">
          <img
            src={branding.companyLogo}
            alt={`${branding.companyName} Logo`}
            className="h-16 w-auto mx-auto"
            style={{ maxHeight: size === 'large' ? '80px' : size === 'medium' ? '64px' : '48px' }}
          />
        </div>
      )}
      
      <h1 
        className={`${titleSizes[size]} font-bold mb-2`}
        style={{ 
          color: 'var(--brand-text-primary)',
          fontFamily: 'var(--brand-font-headings)'
        }}
      >
        {branding.companyName}
      </h1>
      
      {showTagline && (customTagline || branding.tagline) && (
        <p 
          className={`${taglineSizes[size]}`}
          style={{ 
            color: 'var(--brand-text-secondary)',
            fontFamily: 'var(--brand-font-secondary)'
          }}
        >
          {customTagline || branding.tagline}
        </p>
      )}
    </div>
  );
};

// Branded Button Component
interface BrandedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  children: React.ReactNode;
}

export const BrandedButton: React.FC<BrandedButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  className = '',
  children,
  ...props
}) => {
  const { branding } = useBranding();

  const baseClasses = 'font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-4 py-2 text-base',
    large: 'px-6 py-3 text-lg'
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'primary':
        return `text-white hover:opacity-90 focus:ring-opacity-50`;
      case 'secondary':
        return `text-white hover:opacity-90 focus:ring-opacity-50`;
      case 'accent':
        return `text-white hover:opacity-90 focus:ring-opacity-50`;
      case 'outline':
        return `border-2 bg-transparent hover:text-white focus:ring-opacity-50`;
      case 'ghost':
        return `bg-transparent hover:bg-opacity-10 focus:ring-opacity-50`;
      default:
        return '';
    }
  };

  const getVariantStyles = (): React.CSSProperties => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'var(--brand-primary)',
        };
      case 'secondary':
        return {
          backgroundColor: 'var(--brand-secondary)',
        };
      case 'accent':
        return {
          backgroundColor: 'var(--brand-accent)',
        };
      case 'outline':
        return {
          borderColor: 'var(--brand-primary)',
          color: 'var(--brand-primary)',
        };
      case 'ghost':
        return {
          color: 'var(--brand-primary)',
        };
      default:
        return {};
    }
  };

  return (
    <button
      className={`${baseClasses} ${sizeClasses[size]} ${getVariantClasses()} ${className}`}
      style={{
        ...getVariantStyles(),
        borderRadius: 'var(--brand-border-radius)',
        fontFamily: 'var(--brand-font-primary)',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

// Branded Card Component
interface BrandedCardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'small' | 'medium' | 'large';
}

export const BrandedCard: React.FC<BrandedCardProps> = ({
  children,
  className = '',
  padding = 'medium'
}) => {
  const paddingClasses = {
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8'
  };

  return (
    <div
      className={`${paddingClasses[padding]} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/10 transition-colors duration-200 ${className}`}
      style={{
        borderRadius: 'var(--brand-border-radius)',
        fontFamily: 'var(--brand-font-primary)',
      }}
    >
      {children}
    </div>
  );
};

// Branded Input Component
interface BrandedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const BrandedInput: React.FC<BrandedInputProps> = ({
  label,
  error,
  helperText,
  className = '',
  ...props
}) => {
  const { branding } = useBranding();

  return (
    <div className="mb-4">
      {label && (
        <label 
          className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
        >
          {label}
        </label>
      )}
      <input
        className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-opacity-50 focus:border-transparent ${
          error ? 'border-red-500 dark:border-red-400' : ''
        } ${className}`}
        style={{
          borderRadius: 'var(--brand-border-radius)',
          fontFamily: 'var(--brand-font-primary)',
        }}
        {...props}
      />
      {error && (
        <p className="mt-1 text-sm" style={{ color: 'var(--brand-error)' }}>
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm" style={{ color: 'var(--brand-text-muted)' }}>
          {helperText}
        </p>
      )}
    </div>
  );
};

// Branded Select Component
interface BrandedSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: { value: string; label: string }[];
}

export const BrandedSelect: React.FC<BrandedSelectProps> = ({
  label,
  error,
  helperText,
  options,
  className = '',
  ...props
}) => {
  return (
    <div className="mb-4">
      {label && (
        <label 
          className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
        >
          {label}
        </label>
      )}
      <select
        className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-opacity-50 focus:border-transparent ${
          error ? 'border-red-500 dark:border-red-400' : ''
        } ${className}`}
        style={{
          borderRadius: 'var(--brand-border-radius)',
          fontFamily: 'var(--brand-font-primary)',
        }}
        {...props}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1 text-sm" style={{ color: 'var(--brand-error)' }}>
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm" style={{ color: 'var(--brand-text-muted)' }}>
          {helperText}
        </p>
      )}
    </div>
  );
};

// Branded Textarea Component
interface BrandedTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const BrandedTextarea: React.FC<BrandedTextareaProps> = ({
  label,
  error,
  helperText,
  className = '',
  ...props
}) => {
  return (
    <div className="mb-4">
      {label && (
        <label 
          className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
        >
          {label}
        </label>
      )}
      <textarea
        className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-opacity-50 focus:border-transparent resize-none ${
          error ? 'border-red-500 dark:border-red-400' : ''
        } ${className}`}
        style={{
          borderRadius: 'var(--brand-border-radius)',
          fontFamily: 'var(--brand-font-primary)',
        }}
        {...props}
      />
      {error && (
        <p className="mt-1 text-sm" style={{ color: 'var(--brand-error)' }}>
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm" style={{ color: 'var(--brand-text-muted)' }}>
          {helperText}
        </p>
      )}
    </div>
  );
};

// Branded Badge Component
interface BrandedBadgeProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  size?: 'small' | 'medium';
  className?: string;
}

export const BrandedBadge: React.FC<BrandedBadgeProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  className = ''
}) => {
  const sizeClasses = {
    small: 'px-2 py-1 text-xs',
    medium: 'px-3 py-1 text-sm'
  };

  const getVariantStyles = (): React.CSSProperties => {
    const opacity = '0.1';
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: `${CSS.supports('color', 'var(--brand-primary)') ? 'var(--brand-primary)' : '#2563eb'}${opacity}`,
          color: 'var(--brand-primary)',
        };
      case 'secondary':
        return {
          backgroundColor: `${CSS.supports('color', 'var(--brand-secondary)') ? 'var(--brand-secondary)' : '#64748b'}${opacity}`,
          color: 'var(--brand-secondary)',
        };
      case 'success':
        return {
          color: 'var(--brand-success)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
        };
      case 'warning':
        return {
          color: 'var(--brand-warning)',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
        };
      case 'error':
        return {
          color: 'var(--brand-error)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
        };
      default:
        return {};
    }
  };

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full ${sizeClasses[size]} ${className}`}
      style={{
        ...getVariantStyles(),
        borderRadius: 'var(--brand-border-radius)',
        fontFamily: 'var(--brand-font-primary)',
      }}
    >
      {children}
    </span>
  );
};

// Branded Alert Component
interface BrandedAlertProps {
  children: React.ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'error';
  className?: string;
}

export const BrandedAlert: React.FC<BrandedAlertProps> = ({
  children,
  variant = 'info',
  className = ''
}) => {
  const getVariantStyles = (): React.CSSProperties => {
    switch (variant) {
      case 'info':
        return {
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: 'var(--brand-primary)',
          color: 'var(--brand-primary)',
        };
      case 'success':
        return {
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderColor: 'var(--brand-success)',
          color: 'var(--brand-success)',
        };
      case 'warning':
        return {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderColor: 'var(--brand-warning)',
          color: 'var(--brand-warning)',
        };
      case 'error':
        return {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderColor: 'var(--brand-error)',
          color: 'var(--brand-error)',
        };
      default:
        return {};
    }
  };

  return (
    <div
      className={`p-4 border rounded-lg ${className}`}
      style={{
        ...getVariantStyles(),
        borderRadius: 'var(--brand-border-radius)',
        fontFamily: 'var(--brand-font-primary)',
      }}
    >
      {children}
    </div>
  );
};